import type { ZoneType } from "../components/type"
import type { PlayerMetric } from "@/types/metric"

export type ZoneCSVRow = {
  playerName: string
  uniformNumber: number
  metricType: ZoneType
  active: boolean
  customMetricActive: boolean
  maxValue: number | string
  zoneValues: (number | string)[]
}

/**
 * PlayerMetric 배열을 CSV Row 구조로 변환
 * @param zoneCount - 출력할 zone 값의 갯수 (individual range 갯수에 맞춤)
 */
export function buildCSVRows(
  playerMetrics: PlayerMetric[],
  metricType: ZoneType,
  zoneCount: number,
): ZoneCSVRow[] {
  return playerMetrics.map((pm) => ({
    playerName: pm.name ?? "",
    uniformNumber: pm.backNumber ?? 0,
    metricType,
    active: pm.active,
    customMetricActive: pm.customMetricActive ?? false,
    maxValue: pm.max,
    zoneValues: (pm.customMetrics ?? []).slice(0, zoneCount).map((cm) => cm.value),
  }))
}

/**
 * CSV Row 배열을 CSV 문자열로 변환
 */
export function rowsToCSV(rows: ZoneCSVRow[]): string {
  const maxZoneCount = Math.max(...rows.map((r) => r.zoneValues.length), 0)
  const zoneHeaders = Array.from({ length: maxZoneCount }, (_, i) => `zone${i + 1}`)

  const headers = [
    "playerName",
    "uniformNumber",
    "metricType",
    "active",
    "customMetricActive",
    "maxValue",
    ...zoneHeaders,
  ]

  const csvRows = rows.map((row) => {
    const zoneValues = zoneHeaders.map((_, i) => row.zoneValues[i] ?? "")
    return [
      `"${row.playerName}"`,
      row.uniformNumber,
      row.metricType,
      row.active,
      row.customMetricActive,
      row.maxValue,
      ...zoneValues,
    ].join(",")
  })

  return [headers.join(","), ...csvRows].join("\n")
}

/**
 * CSV 파일 다운로드 트리거 (UTF-8 BOM 포함)
 */
export function downloadCSV(content: string, fileName: string) {
  const BOM = "\uFEFF"
  const blob = new Blob([BOM + content], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = fileName.endsWith(".csv") ? fileName : `${fileName}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
